import { Button } from "@/components/ui/Button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/Card";
import { ExtractionRequestError } from "@/features/data-curation/api/curation";
import { ApproveSchemaDialog } from "@/features/data-curation/components/ApproveSchemaDialog";
import type { ChatComposerHandle } from "@/features/data-curation/components/CurationChatPanel";
import { SchemaColumnsEditor } from "@/features/data-curation/components/SchemaColumnsEditor";
import { SchemaColumnsTable } from "@/features/data-curation/components/SchemaColumnsTable";
import {
  useApproveExtractionSchema,
  useReplaceExtractionSchema,
} from "@/features/data-curation/hooks/useCuration";
import { useCurationAgentMessage } from "@/features/data-curation/hooks/useCurationAgentMessage";
import {
  approvalMessage,
  columnsPayload,
  describeColumnChanges,
  hasColumnChanges,
  newColumnDraft,
  toColumnDrafts,
  validateColumnDrafts,
  withUnsentMessage,
  type ColumnDraft,
} from "@/features/data-curation/schemaDraft";
import type { FullRead } from "@/features/data-curation/progress/stages";
import type { ExtractionDataset } from "@/features/data-curation/types";
import { Check, Pencil } from "lucide-react";
import { useEffect, useMemo, useRef, useState, type RefObject } from "react";
import { toast } from "sonner";

type SchemaEdit = {
  /** The save fails with a conflict if the schema changed after this revision. */
  baseRevision: number;
  drafts: ColumnDraft[];
};

const requestErrorMessage = (error: unknown, fallback: string) => {
  if (error instanceof ExtractionRequestError) {
    if (/revision conflict/i.test(error.message)) {
      return "The columns changed while you were working. Look at the latest columns, then try again.";
    }
    if (error.status === 422) {
      return "The server did not accept these columns. Check the labels, types, and examples.";
    }
  }
  return error instanceof Error ? error.message : fallback;
};

const editHint = ({
  problems,
  general,
  changed,
  waitForAgent,
}: {
  problems: number;
  general: string | null;
  changed: boolean;
  waitForAgent: boolean;
}) => {
  if (general) return general;
  if (problems) return `Fix ${problems === 1 ? "1 problem" : `${problems} problems`} before you save.`;
  if (waitForAgent) return "The agent is working. You can save now, and approve when it stops.";
  return changed ? "Your edits are not saved yet." : "You have not changed anything yet.";
};

const viewHint = (canApprove: boolean, agentWorking: boolean) => {
  if (!canApprove) {
    return "Edit the columns here, or ask your agent to change them. To approve, ask your agent to approve the schema. It approves the latest version, with your edits.";
  }
  return agentWorking
    ? "The agent is working. You can approve the columns when it stops."
    : "Edit the columns here, or ask the agent in the chat to change them. When they are correct, approve them.";
};

const fullReadText = (fullRead: FullRead) => {
  const papers =
    fullRead.papers === null
      ? "every paper of the master list"
      : `all ${fullRead.papers.toLocaleString("en-US")} papers of the master list`;
  const pilots =
    fullRead.pilots > 0
      ? ` after ${fullRead.pilots} ${fullRead.pilots === 1 ? "pilot" : "pilots"}`
      : "";
  return `Schema optimization locked these columns${pilots}, so you do not review them again. Approve to read ${papers}: one reader call for each paper, in maps of at most 2,000 papers. It can take hours. To change a column, ask the agent in the chat. A change needs a new pilot.`;
};

export const ProposedSchemaCard = ({
  dataset,
  agentWorking,
  fullRead = null,
  composer,
}: {
  dataset: ExtractionDataset;
  agentWorking: boolean;
  /** Comprehensive mode: the columns are locked, so the user approves the read instead. */
  fullRead?: FullRead | null;
  /** The chat box, whose unsent message can go with the start of the collection. */
  composer?: RefObject<ChatComposerHandle | null>;
}) => {
  const columns = useMemo(
    () => [...dataset.columns].sort((left, right) => left.position - right.position),
    [dataset.columns],
  );
  const [edit, setEdit] = useState<SchemaEdit | null>(null);
  const [focusKey, setFocusKey] = useState<string | null>(null);
  const [savedChanges, setSavedChanges] = useState<string[]>([]);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [approveRevision, setApproveRevision] = useState<number | null>(null);
  const [approveStart, setApproveStart] = useState(true);
  const [unsentMessage, setUnsentMessage] = useState("");
  const [sendUnsent, setSendUnsent] = useState(true);
  const [approveError, setApproveError] = useState<string | null>(null);
  const replaceSchema = useReplaceExtractionSchema(dataset.id);
  const approveSchema = useApproveExtractionSchema(dataset.id);
  const messageAgent = useCurationAgentMessage(dataset.chat_session_id);
  const editButtonRef = useRef<HTMLButtonElement>(null);
  const focusEditButton = useRef(false);

  const canApprove = Boolean(dataset.chat_session_id);
  const busy = replaceSchema.isPending || approveSchema.isPending;
  const saving = replaceSchema.isPending && approveRevision === null;
  const validation = useMemo(() => (edit ? validateColumnDrafts(edit.drafts) : null), [edit]);
  const invalid = (validation?.problems ?? 0) > 0;
  const changed = edit ? hasColumnChanges(edit.drafts, columns) : false;
  const stale = edit !== null && !busy && edit.baseRevision !== dataset.revision;

  useEffect(() => {
    if (edit || !focusEditButton.current) return;
    focusEditButton.current = false;
    editButtonRef.current?.focus();
  }, [edit]);

  const startEdit = () => {
    const drafts = toColumnDrafts(columns);
    setSaveError(null);
    setFocusKey(drafts[0]?.key ?? null);
    setEdit({ baseRevision: dataset.revision, drafts });
  };
  /** The approval dialog keeps focus, so only a plain close returns it to the Edit button. */
  const leaveEdit = (returnFocus: boolean) => {
    focusEditButton.current = returnFocus;
    setEdit(null);
    setSaveError(null);
  };
  const closeEdit = () => leaveEdit(true);
  const cancelEdit = () => {
    if (changed && !window.confirm("Discard your edits to the columns?")) return;
    closeEdit();
  };
  const keepEdit = () =>
    setEdit((current) => current && { ...current, baseRevision: dataset.revision });

  const updateDrafts = (update: (drafts: ColumnDraft[]) => ColumnDraft[]) =>
    setEdit((current) => current && { ...current, drafts: update(current.drafts) });
  const changeColumn = (key: string, patch: Partial<ColumnDraft>) =>
    updateDrafts((drafts) =>
      drafts.map((draft) => (draft.key === key ? { ...draft, ...patch } : draft)),
    );
  const removeColumn = (key: string) =>
    updateDrafts((drafts) => drafts.filter((draft) => draft.key !== key));
  const addColumn = () => {
    const draft = newColumnDraft();
    setFocusKey(draft.key);
    updateDrafts((drafts) => [...drafts, draft]);
  };

  /**
   * Returns the saved revision and every change this person saved in this card.
   * An approval that starts the collection saves without a chat note: its
   * message to the agent lists the changes.
   */
  const saveEdit = async (current: SchemaEdit, returnFocus: boolean, chatNote: boolean) => {
    const changes = describeColumnChanges(current.drafts, columns);
    const saved = await replaceSchema.mutateAsync({
      expectedRevision: current.baseRevision,
      columns: columnsPayload(current.drafts, columns),
      chatNote,
    });
    const allChanges = [
      ...savedChanges,
      ...changes.filter((change) => !savedChanges.includes(change)),
    ];
    setSavedChanges(allChanges);
    leaveEdit(returnFocus);
    return { revision: saved.revision, changes: allChanges };
  };

  const save = async () => {
    if (!edit) return;
    setSaveError(null);
    try {
      await saveEdit(edit, true, true);
      toast.success("Columns saved");
    } catch (error) {
      setSaveError(requestErrorMessage(error, "The columns could not be saved."));
    }
  };

  const openApprove = (start: boolean) => {
    setApproveError(null);
    setApproveStart(start);
    setUnsentMessage(start ? (composer?.current?.unsentMessage() ?? "") : "");
    setSendUnsent(true);
    setApproveRevision(edit?.baseRevision ?? dataset.revision);
  };

  /**
   * An approval that starts the collection messages the agent with the column
   * changes. An approval without a start leaves notes in the chat instead.
   */
  const approve = async () => {
    if (approveRevision === null) return;
    setApproveError(null);
    try {
      let revision = approveRevision;
      let changes = savedChanges;
      if (edit && changed) {
        ({ revision, changes } = await saveEdit(edit, false, !approveStart));
        setApproveRevision(revision);
      } else if (edit) {
        leaveEdit(false);
      }
      await approveSchema.mutateAsync({
        expectedRevision: revision,
        startCollection: approveStart,
      });
      setApproveRevision(null);
      if (!approveStart) {
        toast.success("Columns approved. The collection waits until you click Start collection.");
        return;
      }
      const message = approvalMessage(dataset.id, changes);
      const sendsUnsent = Boolean(unsentMessage) && sendUnsent;
      messageAgent(sendsUnsent ? withUnsentMessage(unsentMessage, message) : message);
      if (sendsUnsent) composer?.current?.clear();
      toast.success("Columns approved. The agent starts the collection in the chat.");
    } catch (error) {
      setApproveError(requestErrorMessage(error, "The columns could not be approved."));
    }
  };

  const blocked = busy || invalid || stale;
  const hint = edit
    ? editHint({
        problems: validation?.problems ?? 0,
        general: validation?.general ?? null,
        changed,
        waitForAgent: canApprove && agentWorking,
      })
    : fullRead && canApprove
      ? agentWorking
        ? "The agent is working. You can approve the full read when it stops."
        : "Approve when the cost is right, or ask the agent in the chat to stop."
      : viewHint(canApprove, agentWorking);

  return (
    <Card>
      <CardHeader className="flex flex-row flex-wrap items-start justify-between gap-3">
        <div className="min-w-0">
          <CardTitle className="text-base">
            {fullRead ? "Approve the full read" : "Proposed columns"}
          </CardTitle>
          <CardDescription>
            {edit
              ? "Change any field, and add or remove columns. Then save your edits."
              : fullRead
                ? fullReadText(fullRead)
                : "Review the agent’s schema carefully. You can edit it before you approve it. Approve columns only keeps the chat open, so you can talk with the agent about the scope of the run before the collection starts. Approve and start collection also starts the collection. After approval you can still add, edit, or delete a column."}
          </CardDescription>
          {fullRead && !edit && fullRead.precision !== null && fullRead.recall !== null && (
            <p className="mt-2 text-sm text-zinc-700">
              Search: precision {Math.round(fullRead.precision * 100)}%, recall{" "}
              {Math.round(fullRead.recall * 100)}% on the labelled sample.
            </p>
          )}
        </div>
        {!edit && !fullRead && (
          <Button
            ref={editButtonRef}
            variant="outline"
            size="sm"
            onClick={startEdit}
            disabled={busy}
          >
            <Pencil className="h-4 w-4" aria-hidden />
            Edit columns
          </Button>
        )}
      </CardHeader>
      <CardContent className="space-y-5">
        {edit ? (
          <SchemaColumnsEditor
            drafts={edit.drafts}
            errors={validation?.errors ?? {}}
            disabled={busy}
            focusKey={focusKey}
            onChange={changeColumn}
            onRemove={removeColumn}
            onAdd={addColumn}
          />
        ) : (
          <SchemaColumnsTable columns={columns} />
        )}
        {stale && (
          <div
            role="alert"
            className="flex flex-col gap-3 rounded-[3px] border border-amber-200 bg-amber-50 p-3 text-sm text-amber-900 sm:flex-row sm:items-center sm:justify-between"
          >
            <p>
              The columns changed while you were editing, possibly because the
              agent revised them. Keep your edits to replace the new version, or
              discard your edits to see it.
            </p>
            <div className="flex shrink-0 gap-2">
              <Button variant="outline" size="sm" onClick={keepEdit}>
                Keep my edits
              </Button>
              <Button variant="outline" size="sm" onClick={closeEdit}>
                Discard my edits
              </Button>
            </div>
          </div>
        )}
        {saveError && (
          <p
            role="alert"
            className="rounded-[3px] border border-red-200 bg-red-50 p-3 text-sm text-red-700"
          >
            {saveError}
          </p>
        )}
        {/* The chat panel narrows the card, so the footer follows the card's width, not the window's. */}
        <div className="@container border-t border-zinc-100 pt-5">
          <div className="flex flex-col gap-3 @4xl:flex-row @4xl:items-center @4xl:justify-between">
            <p className="min-w-0 max-w-3xl text-xs leading-5 text-zinc-500" aria-live="polite">
              {hint}
            </p>
            <div className="flex flex-wrap gap-2 @4xl:shrink-0 @4xl:justify-end">
              {edit && (
                <>
                  <Button variant="outline" size="sm" onClick={cancelEdit} disabled={busy}>
                    Cancel
                  </Button>
                  <Button
                    variant={canApprove ? "outline" : "default"}
                    size="sm"
                    onClick={() => void save()}
                    loading={saving}
                    disabled={!changed || blocked}
                    aria-label={saving ? "Saving columns" : undefined}
                  >
                    Save changes
                  </Button>
                </>
              )}
              {canApprove && !fullRead && (
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => openApprove(false)}
                  disabled={blocked || agentWorking}
                >
                  {edit && changed ? "Save and approve only" : "Approve columns only"}
                </Button>
              )}
              {canApprove && (
                <Button
                  size="sm"
                  onClick={() => openApprove(true)}
                  disabled={blocked || agentWorking}
                >
                  <Check className="h-4 w-4" aria-hidden />
                  {fullRead
                    ? edit && changed
                      ? "Save and approve"
                      : "Approve the full read"
                    : edit && changed
                      ? "Save, approve, and start"
                      : "Approve and start collection"}
                </Button>
              )}
            </div>
          </div>
        </div>
      </CardContent>
      {canApprove && (
        <ApproveSchemaDialog
          open={approveRevision !== null}
          start={approveStart}
          saveFirst={Boolean(edit && changed)}
          fullRead={Boolean(fullRead)}
          busy={busy}
          agentWorking={agentWorking}
          unsentMessage={unsentMessage}
          sendUnsent={sendUnsent}
          onSendUnsentChange={setSendUnsent}
          error={approveError}
          onConfirm={() => void approve()}
          onClose={() => setApproveRevision(null)}
        />
      )}
    </Card>
  );
};
